import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAppSettings } from '@/hooks/useAppSettings';
import { Loader2, Check } from 'lucide-react';

export const AdminToolFloorplanMaskView: React.FC = () => {
  const { settings, loading } = useAppSettings();
  const [paddingPct, setPaddingPct] = useState(1.5);
  const [featherPct, setFeatherPct] = useState(0.3);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!loading) {
      setPaddingPct(settings.floorplan_mask_padding_pct);
      setFeatherPct(settings.floorplan_mask_feather_pct);
    }
  }, [loading, settings.floorplan_mask_padding_pct, settings.floorplan_mask_feather_pct]);

  const handleSave = async () => {
    setSaving(true);
    const { error } = await supabase
      .from('app_settings')
      .update({
        floorplan_mask_padding_pct: paddingPct,
        floorplan_mask_feather_pct: featherPct,
        updated_at: new Date().toISOString(),
      })
      .eq('id', 1);
    setSaving(false);
    if (error) {
      alert(`Fehler beim Speichern: ${error.message}`);
    } else {
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    }
  };

  if (loading) return <div className="p-6"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="p-6 max-w-2xl">
      <h2 className="text-xl font-bold mb-2">Floorplan-Mask-Tuning</h2>
      <p className="text-sm text-slate-600 mb-6">
        Steuert Padding und Feathering der Mask-Generation im Floorplan-Refinement (OpenAI <code>/v1/images/edits</code>).
        Werte sind Prozent der kürzeren Bildkante. Änderungen wirken sofort beim nächsten Refinement-Aufruf.
      </p>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Padding: {paddingPct.toFixed(1)} % ({Math.round((paddingPct / 100) * 1024)} px bei 1024er Bild)
          </label>
          <input
            type="range" min={0} max={10} step={0.1}
            value={paddingPct}
            onChange={(e) => setPaddingPct(parseFloat(e.target.value))}
            className="w-full"
          />
          <p className="text-xs text-slate-500 mt-1">
            Erweitert die Edit-Region. Verhindert "Sticker-Effekt" am Mask-Rand. Default 1.5 %.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Feathering: {featherPct.toFixed(1)} % ({Math.round((featherPct / 100) * 1024)} px bei 1024er Bild)
          </label>
          <input
            type="range" min={0} max={5} step={0.1}
            value={featherPct}
            onChange={(e) => setFeatherPct(parseFloat(e.target.value))}
            className="w-full"
          />
          <p className="text-xs text-slate-500 mt-1">
            Weicher Übergang am Mask-Rand. Verhindert "jagged edges". Default 0.3 %.
          </p>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : savedAt ? <Check className="w-4 h-4" /> : null}
          {savedAt ? 'Gespeichert' : 'Speichern'}
        </button>
      </div>
    </div>
  );
};
